import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AuthShell from "@/pages/AuthShell";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("Password must be at least 6 characters."); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
      toast.success("Password reset successful!");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid Reset Link" footer={<Link to="/forgot-password" className="prose-link font-semibold">Request a new link</Link>}>
        <p className="text-center text-sm text-gray-600">This password reset link is invalid or incomplete.</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a New Password" footer={<Link to="/login" className="prose-link font-semibold">Back to Login</Link>}>
      {done ? (
        <div className="text-center" data-testid="reset-done">
          <CheckCircle2 size={40} className="mx-auto text-[#16A34A]" />
          <p className="mt-3 text-sm text-gray-700">Your password has been reset. Redirecting to login…</p>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <label className="mb-1 block text-sm font-semibold text-[#0B2545]">New Password</label>
            <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full rounded-sm border border-[#D1D5DB] px-3 py-2 text-sm focus:border-[#1F3A68]" data-testid="reset-password" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-semibold text-[#0B2545]">Confirm Password</label>
            <input required type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="w-full rounded-sm border border-[#D1D5DB] px-3 py-2 text-sm focus:border-[#1F3A68]" data-testid="reset-confirm" />
          </div>
          {error && <p className="rounded-sm bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          <button type="submit" disabled={loading} className="flex w-full items-center justify-center gap-2 rounded-sm bg-[#1F3A68] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0B2545] disabled:opacity-60" data-testid="reset-submit">
            {loading && <Loader2 size={16} className="animate-spin" />} Reset Password
          </button>
        </form>
      )}
    </AuthShell>
  );
}
